import {
    IMappingProfile,
    IQueryParams,
    IQueryParamsSymbol,
    PFWmsEntities,
} from '@pf/shared-common';
import { createMap, forMember, mapFrom, Mapper } from '@automapper/core';
import {
    entitySymbols,
    withArraySearchFilter,
    withDefaultEntityMetadata,
    withDefaultSearchParamsMappingConfiguration,
    withDefaultSearchParamsMetadata,
    withFragmentArraySearchFilter,
    withMetadata,
} from '@pf/shared-utility';
import {
    ExceptionCreate,
    ExceptionDto,
    ExceptionRead,
    ExceptionSearchParams,
} from '../../entities/exception.dto';
import {
    CustomFieldMetadataMap,
    ITypeEntityFragmentSymbol,
    NoteMetadataMap,
    ResourceAuthSymbol,
} from '@pf/shared/util-platform';
import { withWarehouseSearchFilter } from '../wms-mapping.util';

const symbols = entitySymbols(PFWmsEntities.Exception);

withDefaultEntityMetadata<ExceptionDto>(symbols.dto, {
    actualQuantity: Number,
    affectedQuantity: Number,
    approvedByUser: Object,
    assignedToUser: Object,
    customFields: [CustomFieldMetadataMap.symbols.dto],
    exceptionCauseType: ITypeEntityFragmentSymbol,
    exceptionType: Object,
    expectedQuantity: Number,
    item: Object,
    inventoryBundle: Object,
    notes: [NoteMetadataMap.symbols.dto],
    priority: String,
    resolutionNotes: [NoteMetadataMap.symbols.dto],
    resolutionType: ITypeEntityFragmentSymbol,
    resolvedDateTime: String,
    resourceAuth: ResourceAuthSymbol,
    slot: Object,
    source: Object,
    sourceEntity: String,
    sourceEntityId: String,
    status: String,
    warehouse: Object,
});

withDefaultEntityMetadata<ExceptionRead>(symbols.entity, {
    actualQuantity: Number,
    affectedQuantity: Number,
    approvedByUser: Object,
    assignedToUser: Object,
    customFields: [CustomFieldMetadataMap.symbols.entity],
    exceptionCauseType: ITypeEntityFragmentSymbol,
    exceptionType: Object,
    expectedQuantity: Number,
    item: Object,
    inventoryBundle: Object,
    notes: [NoteMetadataMap.symbols.entity],
    priority: String,
    resolutionNotes: [NoteMetadataMap.symbols.entity],
    resolutionType: ITypeEntityFragmentSymbol,
    resolvedDateTime: String,
    resourceAuth: ResourceAuthSymbol,
    slot: Object,
    source: Object,
    sourceEntity: String,
    sourceEntityId: String,
    status: String,
    warehouse: Object,
});

withMetadata<ExceptionCreate>(symbols.createBody, {
    actualQuantity: Number,
    affectedQuantity: Number,
    exceptionCauseTypeId: String,
    exceptionTypeId: String,
    expectedQuantity: Number,
    itemId: String,
    inventoryBundleId: String,
    notes: [NoteMetadataMap.symbols.entity],
    priority: String,
    resolutionNotes: [NoteMetadataMap.symbols.entity],
    resolutionTypeId: String,
    resolvedDateTime: String,
    resourceAuth: ResourceAuthSymbol,
    slotId: String,
    sourceEntity: String,
    sourceEntityId: String,
    status: String,
    warehouseId: String,
    assignedToUserId: String,
    approvedByUserId: String,
});

withDefaultSearchParamsMetadata<ExceptionSearchParams>(symbols.searchParams, {
    exceptionTypeId: String,
    exceptionCauseTypeId: String,
    status: String,
    priority: String,
    warehouseId: String,
    slotId: String,
    itemId: String,
    sourceEntity: String,
    sourceEntityId: String,
    resolutionTypeId: String,
    assignedToUserId: String,
    approvedByUserId: String,
});

export const ExceptionMetadataMap: IMappingProfile = {
    symbols,
    profile: (mapper: Mapper) => {
        createMap<ExceptionRead, ExceptionDto>(
            mapper,
            symbols.entity,
            symbols.dto,
            forMember(
                d => d.sourceEntity,
                mapFrom(s => {
                    return s.sourceEntity;
                })
            ),
            forMember(
                d => d.sourceEntityId,
                mapFrom(s => {
                    return s.sourceEntityId;
                })
            ),
            forMember(
                d => d.source,
                mapFrom(s => {
                    return s.source;
                })
            )
        );
        createMap<ExceptionDto, ExceptionRead>(
            mapper,
            symbols.dto,
            symbols.entity,
            forMember(
                d => d.sourceEntity,
                mapFrom(s => {
                    return s.sourceEntity;
                })
            ),
            forMember(
                d => d.sourceEntityId,
                mapFrom(s => {
                    return s.sourceEntityId;
                })
            ),
            forMember(
                d => d.source,
                mapFrom(s => {
                    return s.source;
                })
            )
        );
        createMap<ExceptionDto, ExceptionCreate>(
            mapper,
            symbols.dto,
            symbols.createBody,
            forMember(
                d => d.sourceEntity,
                mapFrom(s => {
                    return s.sourceEntity;
                })
            ),
            forMember(
                d => d.sourceEntityId,
                mapFrom(s => {
                    return s.sourceEntityId;
                })
            ),
            forMember(
                d => d.exceptionTypeId,
                mapFrom(s => {
                    return s.exceptionType?.id;
                })
            ),
            forMember(
                d => d.exceptionCauseTypeId,
                mapFrom(s => {
                    return s.exceptionCauseType?.id;
                })
            ),
            forMember(
                d => d.resolutionTypeId,
                mapFrom(s => {
                    return s.resolutionType?.id;
                })
            ),
            forMember(
                d => d.warehouseId,
                mapFrom(s => {
                    return s.warehouse?.id;
                })
            ),
            forMember(
                d => d.slotId,
                mapFrom(s => {
                    return s.slot?.id;
                })
            ),
            forMember(
                d => d.itemId,
                mapFrom(s => {
                    return s.item?.id;
                })
            ),
            forMember(
                d => d.inventoryBundleId,
                mapFrom(s => {
                    return s.inventoryBundle?.id;
                })
            ),
            forMember(
                d => d.assignedToUserId,
                mapFrom(s => {
                    return s.assignedToUser?.id;
                })
            ),
            forMember(
                d => d.approvedByUserId,
                mapFrom(s => {
                    return s.approvedByUser?.id;
                })
            ),
            forMember(
                d => d.actualQuantity,
                mapFrom(s => s.actualQuantity)
            ),
            forMember(
                d => d.expectedQuantity,
                mapFrom(s => s.expectedQuantity)
            ),
            forMember(
                d => d.affectedQuantity,
                mapFrom(s => s.affectedQuantity)
            )
        );
        createMap<IQueryParams<ExceptionDto>, ExceptionSearchParams>(
            mapper,
            IQueryParamsSymbol,
            symbols.searchParams,
            withArraySearchFilter('status'),
            withArraySearchFilter('sourceEntity'),
            withArraySearchFilter('sourceEntityId'),
            withFragmentArraySearchFilter('exceptionType'),
            withFragmentArraySearchFilter('exceptionCauseType'),
            withFragmentArraySearchFilter('resolutionType'),
            withFragmentArraySearchFilter('slot'),
            withFragmentArraySearchFilter('item'),
            withFragmentArraySearchFilter('assignedToUser'),
            withFragmentArraySearchFilter('approvedByUser'),
            withWarehouseSearchFilter(),
            ...withDefaultSearchParamsMappingConfiguration()
        );

        NoteMetadataMap.profile(mapper);
        CustomFieldMetadataMap.profile(mapper);
    },
};
